/**
 * The payroll slice of the wire, and the paths the payroll screen calls — Task 16's
 * `/api/v1/payroll/*` surface (`src/modules/payroll/routes.ts`), Phase 8a.
 *
 * These types are **hand-written to mirror the payroll module's routes and
 * `service.ts` render functions** — `openapi.yaml` is not edited by this task, so
 * there is no generator to run against it. Every interface below is transcribed from
 * `renderTemplate` / `renderComponentDef` / `renderPeriod` / `renderRun` /
 * `renderItem` in `src/modules/payroll/service.ts`, field by field, the same
 * discipline `api/hr.ts`'s own header describes: transcribe the slice a screen
 * actually reads, not a full copy of a contract this task does not touch.
 *
 * ── The two-step approval state machine ───────────────────────────────────────
 *
 *   draft -> calculated -> manager_approved -> ceo_approved -> paid
 *
 * `calculate` may run again while `draft` or `calculated`; it refuses once a run
 * carries any approval. Only `manager-approve` moves `calculated -> manager_approved`,
 * only `ceo-approve` moves `manager_approved -> ceo_approved`, and the server refuses
 * a ceo-approve where the two approver ids would be the same employee — see
 * `src/modules/payroll/service.ts`'s own header. This client never re-derives that
 * table; the run's `status` on the wire is the only fact this screen renders from.
 *
 * ── `my-payslip` never sends an employee id, and that is the whole point ────────
 *
 * `src/modules/payroll/service.ts`'s own header: "`myPayslip` never accepts an
 * `employee_id` — there is no such parameter — the subject is always
 * `actor.employeeId`, the session. This is a structural guarantee, not a scope check
 * that could have a bug." `payrollMyPayslipPath` below mirrors that structurally: its
 * only parameter is `periodId`, so there is no argument position a caller could use
 * to name a different employee even by mistake.
 *
 * A payslip is released to `GET my-payslip` only for a `ceo_approved` (or `paid`)
 * run — a `draft`/`calculated`/`manager_approved` run's numbers are never handed to
 * the employee they are about, which is why "my payslip" for an in-progress period
 * reads as `data: null` rather than a `404`.
 */

import type { WorkforceApiError } from './errors';
import { qs } from './paths';

/* ───────────────────────────────────── templates + component defs (Task 16) ── */

/** `PayrollComponentDef.kind` — `PAYROLL_COMPONENT_KINDS` of `src/modules/payroll/repo.ts`. */
export const PAYROLL_COMPONENT_KINDS = [
  'earning',
  'deduction',
  'employer_cost',
  'input',
  'derived',
] as const;

export type PayrollComponentKind = (typeof PAYROLL_COMPONENT_KINDS)[number];

/** `PayrollComponentDef.source` — `PAYROLL_COMPONENT_SOURCES` of `src/modules/payroll/repo.ts`. */
export const PAYROLL_COMPONENT_SOURCES = [
  'constant',
  'contract_field',
  'hours',
  'formula',
] as const;

export type PayrollComponentSource = (typeof PAYROLL_COMPONENT_SOURCES)[number];

/**
 * `PayrollTemplate` — `renderTemplate` of `service.ts`. A company's salary-component
 * template (config, not data about any one employee) — gated on
 * `payroll.manage_templates` for both the read and the write (`authorizer.ts`'s
 * `createSessionTemplateViewAuthorizer` uses the SAME code as
 * `createSessionTemplateManageAuthorizer`, unlike the leave/performance screens'
 * split view/manage codes).
 */
export interface PayrollTemplate {
  readonly id: string;
  readonly name: string;
  readonly is_active: boolean;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `TemplateCreateInput` (`service.ts`'s `templateCreateSchema`). Only `name` is
 * required; `is_active` and `seed_achau_defaults` are present-or-absent. Left
 * unset, `seed_achau_defaults` defaults to `true` server-side — a new template is
 * seeded with the Á Châu component list and formula version 1 unless told otherwise.
 */
export interface PayrollTemplateCreateRequest {
  readonly name: string;
  readonly is_active?: boolean;
  readonly seed_achau_defaults?: boolean;
}

/** `PayrollTemplateListResponse` — a bare `{ data: [...] }`, active first then by id. */
export interface PayrollTemplateListResponse {
  readonly data: readonly PayrollTemplate[];
}

/**
 * `PayrollComponentDef` — `renderComponentDef` of `service.ts`. One line of the
 * template: a named component (Lương CB, Lương chính, BHXH nhân viên, …), its `kind`
 * and `source`, and — for a `derived`/`formula` component — the expression that
 * computes it (documentation only; the actual arithmetic lives in
 * `src/modules/payroll/engine.ts`, never re-evaluated on this client).
 */
export interface PayrollComponentDef {
  readonly id: string;
  readonly template_id: string;
  readonly key: string;
  readonly name: string;
  readonly kind: PayrollComponentKind;
  readonly source: PayrollComponentSource;
  readonly formula_expression: string | null;
  readonly sort_order: number;
  /**
   * Migration 051. Non-null means this row's numbers are **not yet confirmed** and
   * the string is the warning to show beside the component; null means there is
   * nothing to warn about. The Vietnam 2026 statutory set
   * (`seedVietnam2026Regulations`) stamps every row it seeds until an accountant
   * signs it off, so a screen that renders the number without the note would be
   * certifying an unconfirmed figure by omission.
   */
  readonly confirmation_note: string | null;
}

/** `ComponentDefCreateInput` (`service.ts`'s `componentDefCreateSchema`). */
export interface PayrollComponentDefCreateRequest {
  readonly key: string;
  readonly name: string;
  readonly kind: PayrollComponentKind;
  readonly source: PayrollComponentSource;
  readonly formula_expression?: string;
  readonly sort_order?: number;
}

/** `PayrollComponentDefListResponse` — a bare `{ data: [...] }`, in `sort_order`. */
export interface PayrollComponentDefListResponse {
  readonly data: readonly PayrollComponentDef[];
}

/* ────────────────────────── formula versions (Task 16, U5, U18) ── */

/**
 * ══════════════════════════════════════════════════════════════════════════════
 *  BOTH DIRECTIONS ARE snake_case. THEY WERE NOT ALWAYS.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `POST /payroll/formulas` parses its body with `formulaParamsSchema`
 * (`src/modules/payroll/service.ts`), whose keys are `gio_chuan`, `bhxh_nv_pct`,
 * `pit_brackets` … — snake_case, and `openapi.yaml`'s `PayrollFormulaParams` is that
 * schema. `createFormulaVersion` renames every key to camelCase on the way into the
 * column, because `wp_payroll_formulas.params` is the jsonb the engine's own
 * `PayrollFormulaParams` interface reads (`engine.ts`, `gioChuan`, `bhxhNvPct`).
 *
 * `renderFormula` used to hand that column back **verbatim**, so the same schema name
 * covered two different spellings on the two directions and the document said so
 * nowhere: `PayrollFormula.params` `$ref`s the snake_case `PayrollFormulaParams`
 * (`additionalProperties: false`, eight required keys) while the server sent camelCase
 * — every response invalid against its own schema. QA-E-044 found it and
 * `renderFormulaParams` now undoes the rename on the way out, so what this module reads
 * is the spelling the document declares.
 *
 * `readPayrollFormulaNumber` and `readPayrollFormulaBrackets` still accept **either**
 * spelling, and that is deliberate rather than leftover: they cost one extra lookup, and
 * they are what lets this screen keep working against an older deployment of the API
 * during a rollout. `params` stays typed as the untyped record it is, for the same
 * reason.
 */

/** Which pay a ground's deduction comes off — `PayrollDeductionGround.applies_to`. */
export const PAYROLL_DEDUCTION_APPLIES_TO = ['gross_before_tax', 'net_after_tax'] as const;

export type PayrollDeductionAppliesTo = (typeof PAYROLL_DEDUCTION_APPLIES_TO)[number];

/**
 * One lawful ground for a `phat` — `PayrollDeductionGround` of `openapi.yaml`, in the
 * spelling a **request** carries.
 *
 * There is no fixed list and there are no fixed names: which grounds exist, what each is
 * called and whether it is `capped` is one tenant's own configuration, versioned with the
 * rest of `params` (KTD6). `code` is what `PayrollEmployeeInput.phat_can_cu` must match.
 */
export interface PayrollDeductionGround {
  readonly code: string;
  readonly name: string;
  readonly applies_to: PayrollDeductionAppliesTo;
  /** Measured against `deduction_cap_pct`; `net_after_tax` grounds only. */
  readonly capped: boolean;
}

/** One row of the progressive PIT tariff, in the spelling a **request** carries. */
export interface PayrollPitBracket {
  /** The bracket's upper bound in VND/month; `null` is the open top bracket. */
  readonly up_to_vnd: number | null;
  /** A fraction — `0.05` is 5%, never `5`. */
  readonly rate: number;
}

/**
 * The eight parameters every version must carry — `formulaParamsSchema`'s required
 * keys, in the document's order.
 */
export const PAYROLL_FORMULA_REQUIRED_PARAMS = [
  'gio_chuan',
  'ot_coefficient',
  'bhxh_nv_pct',
  'bhyt_nv_pct',
  'kpcd_nv_pct',
  'bhxh_cty_pct',
  'bhyt_cty_pct',
  'kpcd_cty_pct',
] as const;

/**
 * The six optional statutory scalars U5 added. Optional because every formula version
 * written before they existed is still a legal, readable version — see
 * `PayrollFormulaParams`'s own description in `openapi.yaml`.
 */
export const PAYROLL_FORMULA_OPTIONAL_PARAMS = [
  'bhtn_nv_pct',
  'bhtn_cty_pct',
  'bhxh_bhyt_cap_vnd',
  'bhtn_cap_vnd',
  'pit_personal_deduction_vnd',
  'pit_dependent_deduction_vnd',
] as const;

/** Every parameter that is a single number. `pit_brackets` is the one that is not. */
export const PAYROLL_FORMULA_SCALAR_PARAMS = [
  ...PAYROLL_FORMULA_REQUIRED_PARAMS,
  ...PAYROLL_FORMULA_OPTIONAL_PARAMS,
] as const;

export type PayrollFormulaRequiredParam = (typeof PAYROLL_FORMULA_REQUIRED_PARAMS)[number];
export type PayrollFormulaOptionalParam = (typeof PAYROLL_FORMULA_OPTIONAL_PARAMS)[number];
export type PayrollFormulaScalarParam = PayrollFormulaRequiredParam | PayrollFormulaOptionalParam;

/**
 * `PayrollFormula` — `renderFormula` of `service.ts`. One VERSIONED parameter set whose
 * `params` and `version` never change: there is no `PATCH` and no `DELETE`, so a run
 * already calculated under an earlier version keeps re-explaining itself
 * (`wp_payroll_runs.formula_version`). Since roadmap E0-3 its LIFECYCLE does change —
 * `is_active`, and the three migration-060 fields a second person's confirmation writes.
 *
 * `params` is deliberately not a typed object — see the block comment above. Read it
 * with `readPayrollFormulaNumber` / `readPayrollFormulaBrackets`.
 */
export interface PayrollFormula {
  readonly id: string;
  readonly version: number;
  readonly params: Readonly<Record<string, unknown>>;
  /**
   * Activated. Several versions can be activated at once, each governing its own stretch
   * of dates; which one is in force on a date is the server's selection
   * (`readFormulaEffectiveOn`), and `GET /payroll/formulas` answers it for today.
   */
  readonly is_active: boolean;
  /**
   * The first date this version's numbers apply. A draft names it
   * (`PayrollFormulaDraftCreateRequest.effective_from`); a version written without one
   * takes the day it was written (040's `CURRENT_DATE`).
   */
  readonly effective_from: string;
  /**
   * Migration 051 — the WARNING the ops seed stamps on numbers nobody has checked;
   * `null` for every version a person wrote. It is **not** the confirmed/unconfirmed
   * fact: migration 060 never clears it, so history keeps the warning beside its
   * answer. Read `confirmed_note` for whether anybody confirmed.
   */
  readonly confirmation_note: string | null;
  /** The author. `null` for a version the ops seed wrote (`seedVietnam2026Regulations`). */
  readonly created_by: string | null;
  /** Migration 060 — the second person who confirmed it (and, if it was inactive, activated it). */
  readonly activated_by: string | null;
  readonly activated_at: string | null;
  /**
   * Migration 060 — the confirmer's own statement of what they checked, against which
   * instrument. **Non-null is exactly "confirmed".** A draft carries `null` until a
   * second person answers it through `POST /payroll/formulas/{version}/activate`.
   */
  readonly confirmed_note: string | null;
  readonly created_at: string;
}

/** The brackets half of a draft, split out so the draft can be an intersection. */
export interface PayrollFormulaBracketsField {
  readonly pit_brackets: readonly PayrollPitBracket[];
}

/**
 * What the formula screen's draft may hold, keyed by the **wire** (request) spelling
 * so that `fieldsOf`'s keys and the draft's keys join — the untyped correspondence
 * U15 reported on `useVersionedConfig`.
 */
export type PayrollFormulaDraft = Record<PayrollFormulaScalarParam, number> &
  PayrollFormulaBracketsField;

/** `formulaParamsSchema` — eight required numbers, six optional ones, and the tariff. */
export type PayrollFormulaCreateParams = Record<PayrollFormulaRequiredParam, number> &
  Partial<Record<PayrollFormulaOptionalParam, number>> &
  Partial<PayrollFormulaBracketsField>;

/**
 * `FormulaCreateInput` (`service.ts`'s `formulaCreateSchema`, `.strict()`), in the two
 * shapes the server accepts — a union, so the E0-3 rule is a type error here rather
 * than a `400` there: `effective_from` rides ONLY with `is_active: false`.
 *
 *  - `{ params }` — active at once. The backend keeps it until roadmap E1 so older
 *    clients do not break. Since roadmap E0-4 no screen sends it: a version one person
 *    makes active by themselves is the gap E0-3 exists to close.
 *  - `{ params, effective_from?, is_active: false }` — a DRAFT, which a second person
 *    adopts through `POST /payroll/formulas/{version}/activate`.
 *
 * `confirmation_note` is never a request field: only the ops seed writes a warning.
 */
export type PayrollFormulaCreateRequest =
  PayrollFormulaImmediateCreateRequest | PayrollFormulaDraftCreateRequest;

export interface PayrollFormulaImmediateCreateRequest {
  readonly params: PayrollFormulaCreateParams;
}

export interface PayrollFormulaDraftCreateRequest {
  readonly params: PayrollFormulaCreateParams;
  /** `YYYY-MM-DD`. Omitted, the server dates the draft the day it writes it. */
  readonly effective_from?: string;
  readonly is_active: false;
}

/** `gio_chuan` → `gioChuan`, `pit_personal_deduction_vnd` → `pitPersonalDeductionVnd`. */
export function payrollFormulaStoredKey(wireKey: string): string {
  return wireKey.replace(/_([a-z0-9])/g, (_match, char: string) => char.toUpperCase());
}

/** The inverse: `gioChuan` → `gio_chuan`, `appliesTo` → `applies_to`. Already-wire keys pass through. */
export function payrollFormulaWireKey(storedKey: string): string {
  return storedKey.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);
}

function wireValue(value: unknown): unknown {
  if (Array.isArray(value)) return (value as readonly unknown[]).map(wireValue);
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {};
    for (const [key, member] of Object.entries(value)) {
      out[payrollFormulaWireKey(key)] = wireValue(member);
    }
    return out;
  }
  return value;
}

/**
 * A whole `params` blob in the spelling a **request** carries, keys nested inside
 * `pit_brackets` and `deduction_grounds` included (`appliesTo` → `applies_to`).
 *
 * The same rollout tolerance `readPayrollFormulaNumber` has, and for the same reason:
 * since QA-E-044's fix `renderFormula` hands back snake_case, where an older deployment
 * hands back the column's camelCase, and this screen has to keep working against both.
 * A key already in the wire spelling passes through unchanged, so running it over a
 * modern response is a no-op.
 *
 * It exists because a formula version is an immutable FULL-PARAMETER write: composing a
 * new version means re-sending every parameter of the old one, including the parameters
 * this screen has no field for (QA-G-017).
 */
export function payrollFormulaWireParams(
  params: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
  return wireValue(params) as Readonly<Record<string, unknown>>;
}

/**
 * One numeric parameter off a formula's `params`, in **either** spelling, or `null`
 * when the version does not carry it.
 *
 * `null` and `0` are different facts and are kept different: a company whose KPCĐ rate
 * really is zero has a number, and a version written before BHTN existed has none.
 */
export function readPayrollFormulaNumber(
  params: Readonly<Record<string, unknown>>,
  wireKey: string,
): number | null {
  for (const key of [wireKey, payrollFormulaStoredKey(wireKey)]) {
    const value = params[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

/** The tariff off a formula's `params`, normalised to the request spelling, or `null`. */
export function readPayrollFormulaBrackets(
  params: Readonly<Record<string, unknown>>,
): readonly PayrollPitBracket[] | null {
  const raw = params.pit_brackets ?? params.pitBrackets;
  if (!Array.isArray(raw)) return null;
  const brackets: PayrollPitBracket[] = [];
  for (const entry of raw as readonly unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const row = entry as Readonly<Record<string, unknown>>;
    const bound = row.up_to_vnd ?? row.upToVnd ?? null;
    const { rate } = row;
    if (typeof rate !== 'number') return null;
    if (bound !== null && typeof bound !== 'number') return null;
    brackets.push({ up_to_vnd: bound, rate });
  }
  return brackets;
}

/**
 * This version's deduction grounds off its `params`, normalised to the request spelling,
 * or `null` when the version defines none.
 *
 * `null` is not `[]` and the difference matters: a version with no grounds refuses every
 * `phat` on a run stamped with it (`openapi.yaml`, `PayrollFormulaParams`), so a screen
 * must be able to say "this version configures none" rather than draw an empty list that
 * reads as "not loaded". A malformed entry gives `null` for the whole set, the same way
 * `readPayrollFormulaBrackets` refuses a half-read tariff.
 */
export function readPayrollFormulaGrounds(
  params: Readonly<Record<string, unknown>>,
): readonly PayrollDeductionGround[] | null {
  const raw = params.deduction_grounds ?? params.deductionGrounds;
  if (!Array.isArray(raw)) return null;
  const grounds: PayrollDeductionGround[] = [];
  for (const entry of raw as readonly unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const row = entry as Readonly<Record<string, unknown>>;
    const { code, name, capped } = row;
    const appliesTo = row.applies_to ?? row.appliesTo;
    if (typeof code !== 'string' || typeof name !== 'string' || typeof capped !== 'boolean') {
      return null;
    }
    if (!(PAYROLL_DEDUCTION_APPLIES_TO as readonly unknown[]).includes(appliesTo)) return null;
    grounds.push({ code, name, applies_to: appliesTo as PayrollDeductionAppliesTo, capped });
  }
  return grounds;
}

export const PAYROLL_FORMULAS_PATH = '/api/v1/payroll/formulas';

/**
 * Is this read refusal "no version is in force", or is it a build that does not serve
 * the route?
 *
 * `GET /payroll/formulas` returns **one** formula — the version in force today — and
 * answers a company with no ACTIVATED version by throwing `404 WORKFORCE_NOT_FOUND`
 * (`readActivePayrollFormula`, `src/modules/payroll/service.ts`). Drafts do not count,
 * so this means "nothing in force yet", never "no history": `GET
 * /payroll/formulas/versions` answers the history, with `{ data: [] }` when empty.
 *
 * The predicate is **both** the code and `details.resource`, and the second half is
 * the load-bearing one. Every `404` in this product carries `WORKFORCE_NOT_FOUND`,
 * including the one a mis-mounted router or a renamed path produces; matching the code
 * alone would turn a routing bug into a friendly "no formula yet, press Create" and
 * the create would then fail against the same absent route. `details.resource` is set
 * at exactly one throw site in the whole payroll module, so it names this fact and
 * nothing else.
 */
export function isPayrollFormulaAbsent(error: WorkforceApiError): boolean {
  return error.code === 'WORKFORCE_NOT_FOUND' && error.details.resource === 'payroll_formula';
}

/* ─────────────────────────── the version history and its confirmation (E0-3, E0-4) ── */

/** `GET /payroll/formulas/versions` — every version, newest first. */
export const PAYROLL_FORMULA_VERSIONS_PATH = `${PAYROLL_FORMULAS_PATH}/versions`;

/** `PayrollFormulaListResponse` — a bare `{ data: [...] }`, newest version first. */
export interface PayrollFormulaListResponse {
  readonly data: readonly PayrollFormula[];
}

/** `POST /payroll/formulas/{version}/activate` — keyed by the version NUMBER, not the row id. */
export function payrollFormulaActivatePath(version: number): string {
  return `${PAYROLL_FORMULAS_PATH}/${String(version)}/activate`;
}

/**
 * The bounds of `formulaActivateSchema`'s `confirmation` (`service.ts`):
 * `z.string().trim().min(10).max(2000)` — trimmed before it is measured (final E0
 * review, R13). Transcribed so the confirmer learns the rule from the form rather than
 * from a `400`, and pinned by `payroll.test.ts`, which reads the server file.
 */
export const PAYROLL_FORMULA_CONFIRMATION_MIN = 10;
export const PAYROLL_FORMULA_CONFIRMATION_MAX = 2000;

/** `PayrollFormulaActivateRequest` — `.strict()`, one field. */
export interface PayrollFormulaActivateRequest {
  /** What the confirmer checked, against which instrument. Stored as `confirmed_note`. */
  readonly confirmation: string;
}

/**
 * Would `formulaActivateSchema` accept this statement? Measured on the TRIMMED text —
 * what the screen sends, and what the server measures. A JSON body, not a header, so
 * Vietnamese is welcome here — unlike `X-Access-Reason` (`access/reason.ts`).
 */
export function isUsableConfirmation(text: string): boolean {
  const trimmed = text.trim();
  return (
    trimmed.length >= PAYROLL_FORMULA_CONFIRMATION_MIN &&
    trimmed.length <= PAYROLL_FORMULA_CONFIRMATION_MAX
  );
}

/**
 * The server's own reason on a refusal — `details.reason`. On a `409
 * WORKFORCE_INVALID_STATE_TRANSITION` from `…/activate` it is the only place the refusal
 * says which rule fired: "this version is already confirmed", "the confirmer must differ
 * from the person who wrote the version", or the version-versus-date conflict. The
 * code's catalogue sentence is generic, so the screen prints this verbatim, labelled as
 * the server's words — never translated, never re-derived.
 */
export function refusalReasonOf(error: WorkforceApiError): string | null {
  const { reason } = error.details;
  return typeof reason === 'string' && reason.trim() !== '' ? reason : null;
}

/**
 * `WORKFORCE_DEDUCTION_CAP_EXCEEDED`'s own `details` — the four fields the calculate
 * endpoint always carries on that refusal (`service.ts`). `null` when the shape does
 * not match, so a caller reads nothing it cannot vouch for (fix round 1 I5).
 */
export interface DeductionCapExceededDetails {
  readonly employeeId: string;
  readonly requestedVnd: number;
  readonly capVnd: number;
  readonly capPct: number | null;
}

export function deductionCapExceededDetailsOf(
  error: WorkforceApiError,
): DeductionCapExceededDetails | null {
  const { employee_id, requested_vnd, cap_vnd, cap_pct } = error.details;
  if (
    typeof employee_id !== 'string' ||
    typeof requested_vnd !== 'number' ||
    typeof cap_vnd !== 'number'
  ) {
    return null;
  }
  return {
    employeeId: employee_id,
    requestedVnd: requested_vnd,
    capVnd: cap_vnd,
    capPct: typeof cap_pct === 'number' ? cap_pct : null,
  };
}

/**
 * `WORKFORCE_VALIDATION_FAILED`'s `details.reason === 'deduction_exceeds_net_pay'` —
 * a `net_after_tax` deduction that would take `thuc_lanh` below zero (`service.ts`,
 * fix round 1 C1). `null` for every OTHER `WORKFORCE_VALIDATION_FAILED` this client
 * reaches, which do not carry this `reason` or these fields.
 */
export interface DeductionExceedsNetPayDetails {
  readonly employeeId: string;
  readonly requestedVnd: number;
  readonly netVnd: number;
}

export function deductionExceedsNetPayDetailsOf(
  error: WorkforceApiError,
): DeductionExceedsNetPayDetails | null {
  const { reason, employee_id, requested_vnd, net_vnd } = error.details;
  if (
    reason !== 'deduction_exceeds_net_pay' ||
    typeof employee_id !== 'string' ||
    typeof requested_vnd !== 'number' ||
    typeof net_vnd !== 'number'
  ) {
    return null;
  }
  return { employeeId: employee_id, requestedVnd: requested_vnd, netVnd: net_vnd };
}

/**
 * `WORKFORCE_VALIDATION_FAILED`'s `details.reason === 'reduction_exceeds_bonus'` — a
 * `gross_before_tax` `phat` that exceeds `thuong`, the bonus it reduces (`service.ts`,
 * I-5). The sibling of `deduction_exceeds_net_pay` on the other side of the ledger:
 * `null` for every OTHER `WORKFORCE_VALIDATION_FAILED` this client reaches, which do
 * not carry this `reason` or these fields.
 */
export interface ReductionExceedsBonusDetails {
  readonly employeeId: string;
  readonly requestedVnd: number;
  readonly bonusVnd: number;
}

export function reductionExceedsBonusDetailsOf(
  error: WorkforceApiError,
): ReductionExceedsBonusDetails | null {
  const { reason, employee_id, requested_vnd, bonus_vnd } = error.details;
  if (
    reason !== 'reduction_exceeds_bonus' ||
    typeof employee_id !== 'string' ||
    typeof requested_vnd !== 'number' ||
    typeof bonus_vnd !== 'number'
  ) {
    return null;
  }
  return { employeeId: employee_id, requestedVnd: requested_vnd, bonusVnd: bonus_vnd };
}

/**
 * The calculate endpoint's own `details.issues[]` (`service.ts`) — read as just the
 * first issue, since the calculate body's own cross-field rule (an amount needs a
 * basis and a reason) and the "not a configured ground" refusal each carry exactly
 * one. `employeeId` is `null` for the schema-level refusal, which validates the whole
 * body before any one employee is resolved; present for the calculate-time "not a
 * configured ground" refusal (fix round 1 I5/I6).
 */
export interface CalculateValidationIssue {
  readonly employeeId: string | null;
  readonly field: string;
  readonly reason: string;
}

export function calculateValidationIssueOf(
  error: WorkforceApiError,
): CalculateValidationIssue | null {
  const { employee_id, issues } = error.details;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  const [first] = issues as readonly unknown[];
  if (typeof first !== 'object' || first === null) return null;
  const { field, reason } = first as Record<string, unknown>;
  if (typeof field !== 'string' || typeof reason !== 'string') return null;
  return { employeeId: typeof employee_id === 'string' ? employee_id : null, field, reason };
}

/* ──────────────────────────────────────────────────────────── periods (Task 16) ── */

/** `PayrollPeriod.status` — `PAYROLL_PERIOD_STATUSES` of `src/modules/payroll/repo.ts`. */
export const PAYROLL_PERIOD_STATUSES = ['draft', 'open', 'closed'] as const;

export type PayrollPeriodStatus = (typeof PAYROLL_PERIOD_STATUSES)[number];

/** `PayrollPeriod` — `renderPeriod` of `service.ts`. A payroll month, e.g. "Tháng 01/2026". */
export interface PayrollPeriod {
  readonly id: string;
  readonly label: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly status: PayrollPeriodStatus;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `PeriodCreateInput` (`service.ts`'s `periodCreateSchema`). All three fields required. */
export interface PayrollPeriodCreateRequest {
  readonly label: string;
  readonly period_start: string;
  readonly period_end: string;
}

/** `PayrollPeriodListResponse` — a bare `{ data: [...] }`, newest `period_start` first. */
export interface PayrollPeriodListResponse {
  readonly data: readonly PayrollPeriod[];
}

/* ──────────────────────────────────────────────────────────────── runs (Task 16) ── */

/**
 * The one country the payroll engine has rules for — tracker CS-3, the registry in
 * `src/modules/payroll/engines/index.ts`.
 *
 * The server is still the authority: it refuses `POST /payroll/runs` and `calculate`
 * with `409 WORKFORCE_PAYROLL_COUNTRY_UNSUPPORTED` for every other country, whatever
 * this screen believes. This constant exists only so the screen can SAY SO FIRST,
 * beside the company's own `country_code` (`api/company-settings.ts`, already read by
 * this screen for the currency), instead of letting a person fill in a run and learn it
 * from a refusal. There is no field on the runs response to read it from: the list is a
 * bare `{ data: [...] }` with nowhere to hang a flag, and inventing an envelope for one
 * boolean is a contract change this decision does not need.
 */
export const PAYROLL_ENGINE_COUNTRY = 'VN';

/** `PayrollRun.status` — `PAYROLL_RUN_STATUSES` of `src/modules/payroll/repo.ts`. Exactly the five states of the two-step approval state machine (see this file's header). */
export const PAYROLL_RUN_STATUSES = [
  'draft',
  'calculated',
  'manager_approved',
  'ceo_approved',
  'paid',
] as const;

export type PayrollRunStatus = (typeof PAYROLL_RUN_STATUSES)[number];

/**
 * `PayrollRun` — `renderRun` of `service.ts`. `formula_version` is stamped once, at
 * creation, from the company's then-active formula — a later formula version never
 * silently changes what an existing run computes. `manager_approved_by`/`_at` and
 * `ceo_approved_by`/`_at` are `null` until their step happens; both are set together
 * with their approver's id.
 */
export interface PayrollRun {
  readonly id: string;
  readonly period_id: string;
  readonly template_id: string;
  readonly formula_version: number;
  readonly status: PayrollRunStatus;
  readonly manager_approved_by: string | null;
  readonly manager_approved_at: string | null;
  readonly ceo_approved_by: string | null;
  readonly ceo_approved_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `RunCreateInput` (`service.ts`'s `runCreateSchema`). Both fields required. */
export interface PayrollRunCreateRequest {
  readonly period_id: string;
  readonly template_id: string;
}

/** `PayrollRunListResponse` — a bare `{ data: [...] }`, newest (highest id) first. */
export interface PayrollRunListResponse {
  readonly data: readonly PayrollRun[];
}

/**
 * `EmployeeInput` of `service.ts`'s `employeeInputSchema` — one employee's manual,
 * per-period inputs the engine cannot derive from a contract or attendance (Lương CB,
 * Lương năng suất HD, tỷ lệ đạt, phụ cấp, thưởng, phạt, tạm ứng, người phụ thuộc). Every
 * field but `employee_id` is optional and defaults to zero server-side for an employee
 * the caller does not name — that employee still gets a payslip, every manual component
 * at zero. Since roadmap A-6 the Calculate panel builds these from what a person typed
 * (`payroll/employee-inputs.ts`): only typed fields, and never `phat` (roadmap §2).
 */
export interface PayrollEmployeeInput {
  readonly employee_id: string;
  readonly luong_cb?: number;
  readonly luong_nang_suat_hd?: number;
  readonly ty_le_dat?: number;
  readonly phu_cap_dong_phuc?: number;
  readonly phu_cap_xang?: number;
  readonly phu_cap_khac?: number;
  readonly thuong?: number;
  readonly phat?: number;
  /**
   * The `code` of one of the run's formula version's own `deduction_grounds`
   * (`PayrollFormulaParams`), required whenever `phat` is greater than zero. No fixed
   * list here or anywhere in this client: which grounds exist is one company's own
   * configuration, read off the active formula version, not a constant this type could
   * enumerate. A code naming no configured ground is refused server-side, by field.
   */
  readonly phat_can_cu?: string;
  /** What happened, in the payer's own words. Itemised on the payslip. */
  readonly phat_ly_do?: string;
  readonly tam_ung?: number;
  /** Registered dependants (Task U32) — decides `giam_tru_gia_canh` and the PIT withheld. */
  readonly so_nguoi_phu_thuoc?: number;
}

/** `CalculateRunInput` (`service.ts`'s `calculateRunSchema`). */
export interface PayrollCalculateRunRequest {
  readonly employee_inputs?: readonly PayrollEmployeeInput[];
}

/** The `200` body of `POST /payroll/runs/{id}/calculate` — the run, freshly `calculated`, plus every item it wrote. */
export interface PayrollCalculateRunResponse {
  readonly run: PayrollRun;
  readonly items: readonly PayrollItem[];
}

/**
 * `PayrollRunRosterEntry` — `renderRosterEntry` of `service.ts` (roadmap E0-4 / A-6):
 * one employee a calculation of the run pays. `full_name` is `null` when the mirror no
 * longer has the row; the id still names who is paid.
 */
export interface PayrollRosterEntry {
  readonly employee_id: string;
  readonly full_name: string | null;
}

/** `PayrollRunRosterResponse` — `{ data }`, employee id ascending: the order calculate pays in. */
export interface PayrollRosterResponse {
  readonly data: readonly PayrollRosterEntry[];
}

/* ───────────────────────────────────────────────────────────── items (Task 16) ── */

/**
 * `PayrollItem` — `renderItem` of `service.ts`. One employee's payslip for one run:
 * the three headline numbers (`gross` = Tổng lương, `total_deductions`, `net` =
 * Thực lãnh, every one an **exact decimal string**, `NUMERIC(14,2)`, never a JS
 * number) plus `explanation` — the full per-component breakdown
 * (`PayrollComponents` of `src/modules/payroll/engine.ts`) a payslip is explained
 * from. `explanation` is typed as the shape the engine actually writes rather than
 * `Record<string, unknown>`, so the my-payslip panel can read named fields
 * (`luong_chinh`, `luong_hieu_qua`, …) rather than casting at the call site.
 */
export interface PayrollItemExplanation {
  readonly formula_version: number;
  readonly params: Readonly<Record<string, number>>;
  readonly inputs: Readonly<Record<string, unknown>>;
  readonly components: PayrollComponents;
}

/**
 * `PayrollComponents` of `src/modules/payroll/engine.ts` — every named component a
 * payslip explains itself with, rounded to whole đồng. The Vietnamese payroll terms
 * of `docs/payroll/SAMPLE_PAYROLL_ACHAU_VI.md`: Lương CB, Lương chính, Lương Hiệu
 * quả, Lương OT, Phụ cấp, BHXH/BHYT/KPCĐ (nhân viên and công ty), Tổng lương, Thực
 * lãnh.
 */
export interface PayrollComponents {
  readonly luong_cb: number;
  readonly luong_chinh_theo_hd: number;
  readonly gio_cong: number;
  readonly luong_chinh: number;
  readonly luong_nang_suat_hd: number;
  readonly ty_le_dat: number;
  readonly luong_hieu_qua: number;
  readonly gio_ot: number;
  readonly luong_ot: number;
  readonly luong: number;
  readonly phu_cap_dong_phuc: number;
  readonly phu_cap_xang: number;
  readonly phu_cap_khac: number;
  readonly tong_phu_cap: number;
  readonly thuong: number;
  /** The raw echoed input — never itself a component of the answer; see `khau_tru_boi_thuong`/`giam_thuong`. */
  readonly phat: number;
  /** `phat`'s amount when its ground applies to net pay; `0` for a gross-side ground or no ground at all. */
  readonly khau_tru_boi_thuong: number;
  /** `phat`'s amount when its ground applies to gross pay; `0` for a net-side ground or no ground at all. */
  readonly giam_thuong: number;
  /** The configured deduction ceiling this payslip was measured against. `0` = the version carries no ceiling. */
  readonly khau_tru_toi_da: number;
  readonly bhxh_nv: number;
  readonly bhyt_nv: number;
  readonly kpcd_nv: number;
  readonly tong_bhnv: number;
  readonly bhxh_cty: number;
  readonly bhyt_cty: number;
  readonly kpcd_cty: number;
  readonly tong_bh_cty: number;
  readonly tong_bh_kpcd: number;
  readonly tong_luong: number;
  readonly tong_chi_tra: number;
  readonly tam_ung: number;
  readonly thuc_lanh: number;
}

export interface PayrollItem {
  readonly id: string;
  readonly run_id: string;
  readonly employee_id: string;
  readonly gross: string;
  readonly total_deductions: string;
  readonly net: string;
  readonly explanation: PayrollItemExplanation;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `PayrollItemListResponse` — a run's payslips (`GET /payroll/runs/{id}/items`), a bare `{ data: [...] }`, by `employee_id`. */
export interface PayrollItemListResponse {
  readonly data: readonly PayrollItem[];
}

/**
 * `GET /payroll/my-payslip?period_id=` response — `{ data: PayrollItem | null }`.
 * `null` means the period has no `ceo_approved`/`paid` run yet, or none named this
 * employee — not an error, and not the same fact as `404`.
 */
export interface PayrollMyPayslipResponse {
  readonly data: PayrollItem | null;
}

/* ─────────────────────────────────────────────────────────────── path builders ── */

export const PAYROLL_TEMPLATES_PATH = '/api/v1/payroll/templates';
export const PAYROLL_PERIODS_PATH = '/api/v1/payroll/periods';
export const PAYROLL_RUNS_PATH = '/api/v1/payroll/runs';
export const PAYROLL_MY_PAYSLIP_PATH = '/api/v1/payroll/my-payslip';

/** `GET`/`POST /payroll/templates/{id}/component-defs` — one template's component list. */
export function payrollComponentDefsPath(templateId: string): string {
  return `${PAYROLL_TEMPLATES_PATH}/${encodeURIComponent(templateId)}/component-defs`;
}

/**
 * `GET /payroll/runs?period_id=` — every run, optionally narrowed to one period (a
 * **server** filter). `period_id` is omitted entirely rather than sent empty when no
 * period is chosen.
 */
export function payrollRunsPath(periodId?: string): string {
  return `${PAYROLL_RUNS_PATH}${qs({ period_id: periodId })}`;
}

/** `POST /payroll/runs/{id}/calculate` — the deterministic engine, idempotent while `draft`/`calculated`. */
export function payrollRunCalculatePath(runId: string): string {
  return `${PAYROLL_RUNS_PATH}/${encodeURIComponent(runId)}/calculate`;
}

/** `POST /payroll/runs/{id}/manager-approve` — step 1 of 2. */
export function payrollRunManagerApprovePath(runId: string): string {
  return `${PAYROLL_RUNS_PATH}/${encodeURIComponent(runId)}/manager-approve`;
}

/** `POST /payroll/runs/{id}/ceo-approve` — step 2 of 2 (OFFICIAL). */
export function payrollRunCeoApprovePath(runId: string): string {
  return `${PAYROLL_RUNS_PATH}/${encodeURIComponent(runId)}/ceo-approve`;
}

/** `GET /payroll/runs/{id}/items` — a run's payslips, company-wide once the caller holds `payroll.view_payroll` (ruling P1). */
export function payrollRunItemsPath(runId: string): string {
  return `${PAYROLL_RUNS_PATH}/${encodeURIComponent(runId)}/items`;
}

/** `GET /payroll/runs/{id}/roster` — who a calculation of the run pays (`payroll.run_payroll`). */
export function payrollRunRosterPath(runId: string): string {
  return `${PAYROLL_RUNS_PATH}/${encodeURIComponent(runId)}/roster`;
}

/**
 * `GET /payroll/my-payslip?period_id=` — the caller's OWN payslip, self-only.
 *
 * **There is no `employeeId` parameter here, and there must never be one.** The
 * subject is always the session (this file's own header); adding a parameter that
 * could name someone else would be exactly the mistake `service.ts`'s `myPayslip`
 * was written to make structurally impossible on the server, undone on the client.
 */
export function payrollMyPayslipPath(periodId: string): string {
  return `${PAYROLL_MY_PAYSLIP_PATH}${qs({ period_id: periodId })}`;
}

export const PAYROLL_MY_PAYSLIP_PDF_PATH = '/api/v1/payroll/my-payslip.pdf';

/**
 * `GET /payroll/my-payslip.pdf?period_id=` — the caller's OWN released payslip as a PDF.
 * Self-only for the reason `payrollMyPayslipPath` gives: there is no employee id
 * parameter, so no argument position could name anyone else. A sensitive read —
 * `X-Access-Reason` is mandatory (`payroll/payslip-download.ts`).
 */
export function payrollMyPayslipPdfPath(periodId: string): string {
  return `${PAYROLL_MY_PAYSLIP_PDF_PATH}${qs({ period_id: periodId })}`;
}

/**
 * `GET /payroll/runs/{id}/items/{employeeId}/payslip.pdf` — one team payslip, released
 * runs only (`ceo_approved`/`paid`), over the caller's resolved field of view. Sensitive.
 */
export function payrollRunItemPayslipPdfPath(runId: string, employeeId: string): string {
  const run = encodeURIComponent(runId);
  return `${PAYROLL_RUNS_PATH}/${run}/items/${encodeURIComponent(employeeId)}/payslip.pdf`;
}

/**
 * The name a team payslip is saved under — `payslipFilename` of `service.ts`, the name
 * the server's own `Content-Disposition` carries. `client.sensitiveDownload` hands back
 * the bytes without the headers, so the name is rebuilt here from the same two ids.
 */
export function payslipPdfFilename(periodId: string, employeeId: string): string {
  return `payslip-${periodId}-${employeeId}.pdf`;
}

/**
 * My own payslip's file name. The employee id is left out on purpose: `MyPayslipPanel`
 * holds no employee id at all (its own header), and a person saving their own payslip
 * does not need theirs in the name.
 */
export function payrollMyPayslipPdfFilename(periodId: string): string {
  return `payslip-${periodId}.pdf`;
}
